import { effectScope, readonly, ref, watch } from 'vue'
import { useUserStore } from '@/store/userStore'
import {
  AiAssistantBetaService,
  ResolutionEnum,
  type AiAssistantBetaAnnouncementStateResponse
} from '@/types/generated'

/**
 * Per-user announcement state for the AI Assistant Beta opt-in (AAB-002).
 *
 * The server is authoritative — resolution (modal / banner / none) is computed
 * server-side from three persisted fields on the user, so clock skew and
 * stale in-memory state cannot drift the user out of the announcement cadence.
 *
 * The composable is consumed by useAiAssistantBeta to drive modal- and banner-
 * visibility on the case-list and case-detail surfaces. Mutations are recorded
 * via two events (`modal_seen`, `banner_dismissed`) that the server applies
 * idempotently — re-firing an event on a saturated counter is a no-op.
 */

export type AnnouncementResolution = ResolutionEnum

const resolution = ref<AnnouncementResolution>(ResolutionEnum.MODAL)
const modalSeenAt = ref<string | null>(null)
const bannerDismissedAt = ref<string | null>(null)
const bannerDismissCount = ref(0)
const isHydrated = ref(false)
const isLoading = ref(false)

// Generation counter — incremented on every reset() so in-flight requests started
// under a previous user silently discard their response instead of overwriting state
// that now belongs to a different account.
let stateGeneration = 0
let hydratePromise: Promise<void> | null = null

let userResetWatcherInitialized = false
const userResetScope = effectScope(true)

function applyServerState(state: AiAssistantBetaAnnouncementStateResponse) {
  resolution.value = state.resolution
  modalSeenAt.value = state.modal_seen_at
  bannerDismissedAt.value = state.banner_dismissed_at
  bannerDismissCount.value = state.banner_dismiss_count
  isHydrated.value = true
}

function reset() {
  stateGeneration++
  resolution.value = ResolutionEnum.MODAL
  modalSeenAt.value = null
  bannerDismissedAt.value = null
  bannerDismissCount.value = 0
  isHydrated.value = false
  hydratePromise = null
}

function ensureUserResetWatcher(userStore: ReturnType<typeof useUserStore>) {
  if (userResetWatcherInitialized) return
  userResetWatcherInitialized = true
  // Watcher fires on changes only (not the initial value), so first user load is a
  // no-op against the already-default state. Logout (uuid → undefined) and login
  // (undefined → new uuid) both trigger a wipe — by the time the new user is set,
  // state is clean and the next consumer call re-hydrates against the new account.
  userResetScope.run(() => {
    watch(
      () => userStore.user?.uuid,
      () => reset()
    )
  })
}

async function hydrate() {
  if (isHydrated.value || hydratePromise) return hydratePromise ?? Promise.resolve()
  const gen = stateGeneration
  isLoading.value = true
  hydratePromise = (async () => {
    try {
      const state = await AiAssistantBetaService.aiAssistantBetaAnnouncementStateRetrieve()
      if (gen !== stateGeneration) return
      applyServerState(state)
    } catch (error) {
      console.error('[BetaAnnouncement] hydrate() failed', error)
      if (gen !== stateGeneration) return
      // Mark hydrated even on failure so a flapping endpoint doesn't fire one
      // request per consumer mount. Defaults (MODAL, never seen) are the safe
      // fallback — admins who already dismissed will see one extra impression
      // but nothing is lost on the backend. A reset (user switch) clears the
      // flag and retries.
      isHydrated.value = true
    } finally {
      if (gen === stateGeneration) {
        isLoading.value = false
      }
      hydratePromise = null
    }
  })()
  return hydratePromise
}

async function recordEvent(event: 'modal_seen' | 'banner_dismissed') {
  const gen = stateGeneration
  isLoading.value = true
  try {
    const state = await AiAssistantBetaService.aiAssistantBetaAnnouncementEventCreate({
      requestBody: { event }
    })
    if (gen !== stateGeneration) return
    applyServerState(state)
  } catch (error) {
    console.error('[BetaAnnouncement] recordEvent() failed', error, { event })
    throw error
  } finally {
    if (gen === stateGeneration) {
      isLoading.value = false
    }
  }
}

async function markModalSeen() {
  await recordEvent('modal_seen')
}

async function dismissBanner() {
  await recordEvent('banner_dismissed')
}

export function useBetaAnnouncement() {
  const userStore = useUserStore()
  ensureUserResetWatcher(userStore)

  return {
    resolution: readonly(resolution),
    modalSeenAt: readonly(modalSeenAt),
    bannerDismissedAt: readonly(bannerDismissedAt),
    bannerDismissCount: readonly(bannerDismissCount),
    isHydrated: readonly(isHydrated),
    isLoading: readonly(isLoading),
    hydrate,
    markModalSeen,
    dismissBanner
  }
}
